// How the admin editor's save writes a curated exercise DOCUMENT without its
// text (#4285), lifted out of `ExerciseDbService` so it can be unit-tested: the
// service pulls in Angular's HttpClient, which the unit-test runner cannot load.
//
// Dependencies are imported as TYPES only, the same split `exercise-text-cache.ts`
// uses. See design-docs/global-common-localization.md, "Deleting the inline
// fields (#4285)".

import type { DatabaseWrapper, TransactionId } from 'services/database-wrapper';
import { Exercise } from '@cf-shared/data-model/exercise';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { deepClone, deepOverwriteCouchSafe } from '@cf-shared/deep-helpers';
import { EXERCISE_TEXT_FIELDS, ExerciseTextField, withoutInlineExerciseText } from '@cf-shared/data-model/exercise-text-keys';

/** An exercise's text as it was when the save began, one entry per field ('' when blank). */
export type ExerciseTextSnapshot = Record<ExerciseTextField, string>;

/** The three database calls the save needs. */
export type ExerciseDocumentStore = Pick<DatabaseWrapper, 'withDocWatcherAsync' | 'getTypedRecordAsync' | 'saveRecordAsync'>;

/**
 * Save an exercise document. A curated one is written WITHOUT `name` /
 * `shortname` / `description`, whose only home is the `$text/` store; a custom
 * one is written as it is.
 *
 * The curated document written is the STORED object, overwritten with the edit,
 * never the caller's object. The editor usually holds a hydrated copy, and the
 * database wrapper insists every save of one id uses the same object: given a
 * copy it reports a bypass and copies only the keys the copy has, so a field
 * removed from the copy would stay on the stored document.
 *
 * The caller's object may itself BE the stored object (hydration hands back its
 * input when it changed nothing, e.g. when the `$text/` read failed). Then this
 * strips the text off the caller's object too. That is why the text comes back
 * as a snapshot taken first: the caller writes the `$text/` documents from it,
 * never from the object it passed in.
 *
 * If the write fails, the stored object is put back as it was, so readers in
 * this session do not see an edit the database never received — unless the
 * wrapper already merged a newer version into it (its `_rev` moved to a new
 * value), which is then left alone.
 */
export async function saveExerciseDocumentWithoutTextAsync(
  e: Exercise,
  db: ExerciseDocumentStore,
  transaction: TransactionId,
): Promise<ExerciseTextSnapshot> {
  const text = snapshotExerciseText(e);
  const withoutText = withoutInlineExerciseText(e);
  // if it is a custom exercise, its inline text is the only copy and stays
  if (withoutText === e) {
    await db.saveRecordAsync(e, transaction);
    return text;
  }
  const stored = await readStoredExerciseAsync(e._id, db);
  // if nothing is stored yet (the admin is creating it), the stripped copy is the first object
  if (!stored) {
    await db.saveRecordAsync(withoutText, transaction);
    e._rev = withoutText._rev;
    return text;
  }
  const before = deepClone(stored);
  deepOverwriteCouchSafe(stored, withoutText);
  try {
    await db.saveRecordAsync(stored, transaction);
  } catch (err) {
    // if the wrapper moved the revision to a new one, it merged the database's
    // newer version in before failing; the old body under that revision would
    // overwrite the other writer's change on the next save, so leave it.
    // Otherwise (unchanged, or deleted by a vanished-document path) put the
    // old body back under the old revision, so the next save conflicts and is
    // merged rather than written straight through.
    const wasMergedNewer = stored._rev !== undefined && stored._rev !== before._rev;
    if (!wasMergedNewer) {
      deepOverwriteCouchSafe(stored, before);
      stored._rev = before._rev;
    }
    throw err;
  }
  // the editor goes on holding its own object, so give it the revision just
  // written, or its next save would be refused as out of date
  e._rev = stored._rev;
  return text;
}

// the three text fields as they are right now, before anything strips them
function snapshotExerciseText(e: Exercise): ExerciseTextSnapshot {
  const text = {} as ExerciseTextSnapshot;
  for (const field of EXERCISE_TEXT_FIELDS) {
    text[field] = e[field] ?? '';
  }
  return text;
}

// the stored exercise document, or undefined when there is none yet
async function readStoredExerciseAsync(id: string, db: ExerciseDocumentStore): Promise<Exercise | undefined> {
  try {
    return await db.withDocWatcherAsync(watcher => db.getTypedRecordAsync<Exercise>(id, watcher));
  } catch (err) {
    // expected: not-found from getTypedRecordAsync when the exercise is being created
    if (isPouchNotFound(err)) {
      return undefined;
    }
    throw err;
  }
}
